/**
 * Alias layer over design-system/tokens.css — the TS twin of
 * `redesign-colors.module.css`.
 *
 * Values are CSS `var()` references, not hex. They work anywhere a CSS value
 * is expected (inline `style`, inline-SVG `fill`/`stroke`, the `Icon` `color`
 * prop). They are NOT parseable colours — never do string maths on them, and
 * never feed one to a canvas API or a `<meta name="theme-color">`.
 *
 * Never add a hex here. New colour needed? File a DS request.
 */
enum RedesignColors {
  /* Primary */
  darkBlue = "var(--color-text-active)",
  lightBlue = "var(--color-surface-content-secondary)",

  /* Text */
  textPrimary = "var(--color-text-primary)",
  textSecondary = "var(--color-text-secondary)",
  textWhite = "var(--color-text-invert)",
  textPositive = "var(--color-text-positive)",

  /* Semantic */
  interactiveActive = "var(--color-interactive-active)",
  interactiveInactive = "var(--color-interactive-inactive)",
  interactiveSelected = "var(--color-interactive-selected)",
  interactiveUnselected = "var(--color-interactive-unselected)",
  iconPrimary = "var(--color-icon-primary)",
  iconPositive = "var(--color-icon-positive)",
  badgeHighlight = "var(--color-surface-badge-highlight)",

  /* Secondary / Accent */
  linen = "var(--color-surface-background-neutral)",
  /* TODO(DS): pink / yellow / orange only exist under data-viz names. */
  pink = "var(--color-data-viz-warm-pink)",
  green = "var(--color-surface-content-feedback-positive)",
  yellow = "var(--color-data-viz-gold)",
  orange = "var(--color-data-viz-orange)",

  /* Tints / Background */
  tintWhite = "var(--color-surface-content-primary-default)",
  cotton = "var(--color-surface-background-primary-default)",
  tintLightPink = "var(--color-surface-content-alert)",
  tintLightYellow = "var(--color-surface-content-warning)",

  /* Neutrals */
  frost = "var(--color-surface-content-tertiary-default)",
  grey1 = "var(--color-border-subtle)",
  grey2 = "var(--color-border-default)",
  grey3 = "var(--color-text-inactive)",
  grey4 = "var(--color-text-secondary)",
  white = "var(--color-surface-content-primary-default)",

  /* TODO(DS-request): no token covers these five — see the CSS module for why. */
  errorRed = "#ca3342",
  blue = "#a5d9ff",
  tintLightGreen = "#e8f0c3",
  tintLightOrange = "#ffe5c6",
  black = "#01061b",
}

export type RedesignColor = keyof typeof RedesignColors;

export default RedesignColors;
